/* ─────────────────────────────────────────────────────────────────────────
   BLOG — page interactivity (index, post, style guide share this).

   Self-contained, no dependency on the landing's main.js graph. Secondary
   pages deliberately retain native browser scrolling; only the landing page
   initializes Lenis. Reveals use a plain IntersectionObserver (no GSAP).

   Wires:
     • Reveal-on-scroll for [.reveal] (play once, stay visible)
     • Back-to-top button (show past 600px, smooth scroll to top)
     • Newsletter form (no backend — optimistic "you're on the list" state)
   ───────────────────────────────────────────────────────────────────────── */

// Shared top-nav pill (sections/top-nav.html): region-picker CTA + active link.
import './demo-picker.js';
import './top-nav.js';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
};

/* ── Reveal-on-scroll ──────────────────────────────────────────────────── */
const revealEls = document.querySelectorAll('.reveal');
if (revealEls.length) {
  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target); // play once, stay
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
  );
  revealEls.forEach((el) => io.observe(el));
}

/* ── Back-to-top ───────────────────────────────────────────────────────── */
const toTop = document.querySelector('[data-totop]');
if (toTop) {
  toTop.addEventListener('click', scrollToTop);
  const onScroll = () => toTop.classList.toggle('is-visible', window.scrollY > 600);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ── Collapsible nav — hide on scroll-down, show on scroll-up / top / end ─ */
const collapseNav = document.querySelector('.nav-collapse');
if (collapseNav) {
  let lastY = window.scrollY;
  let ticking = false;
  // Inline `translate` (not a class) — proven to win the cascade here, where
  // a `transform`/`top` override on the header swallows the class rule.
  const setHidden = (hide) => {
    collapseNav.style.translate = hide ? '0 -110px' : '0 0';
  };
  const sync = () => {
    const y = Math.max(0, window.scrollY);
    const nearTop = y < 120;
    const atEnd = window.innerHeight + y >= document.documentElement.scrollHeight - 8;
    if (nearTop || atEnd) {
      setHidden(false);
    } else if (y > lastY + 6) {
      setHidden(true); // scrolling down
    } else if (y < lastY - 6) {
      setHidden(false); // scrolling up
    }
    lastY = y;
    ticking = false;
  };
  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(sync);
      ticking = true;
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ── Mobile nav — full-screen drawer ───────────────────────────────────── */
const menuToggle = document.querySelector('[data-menu-toggle]');
const drawer = document.querySelector('[data-mobile-menu]');
const menuClose = document.querySelector('[data-menu-close]');
if (menuToggle && drawer) {
  const setOpen = (open) => {
    drawer.classList.toggle('is-open', open);
    menuToggle.setAttribute('aria-expanded', String(open));
    // lock the page behind the full-screen drawer
    document.documentElement.style.overflow = open ? 'hidden' : '';
  };
  menuToggle.addEventListener('click', () => setOpen(true));
  menuClose?.addEventListener('click', () => setOpen(false));
  // close after navigating (in-page anchors) or on Escape
  drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setOpen(false);
  });
}

/* ── Table of contents scroll-spy (post page) ──────────────────────────── */
const tocLinks = [...document.querySelectorAll('[data-toc-link]')];
if (tocLinks.length) {
  const sections = tocLinks
    .map((l) => document.querySelector(l.getAttribute('href')))
    .filter(Boolean);
  const setActive = (id) => {
    tocLinks.forEach((l) => l.classList.toggle('is-active', l.getAttribute('href') === '#' + id));
  };
  const spy = new IntersectionObserver(
    (entries) => {
      // Pick the heading nearest the top that's in/above the trigger band.
      const visible = entries.filter((e) => e.isIntersecting);
      if (visible.length) {
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActive(visible[0].target.id);
      }
    },
    { rootMargin: '-88px 0px -70% 0px', threshold: 0 },
  );
  sections.forEach((s) => spy.observe(s));
  // Default to the first section before any scroll.
  if (sections[0]) setActive(sections[0].id);
}

/* ── Social share links — point each network button at the live article ── */
{
  const url = encodeURIComponent(location.href);
  const text = encodeURIComponent(document.title);
  const nets = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    x: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
  };
  document.querySelectorAll('[data-share-net]').forEach((a) => {
    const href = nets[a.dataset.shareNet];
    if (href) {
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
  });
}

/* ── Newsletter (optimistic, no backend) ───────────────────────────────── */
document.querySelectorAll('[data-newsletter]').forEach((form) => {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = form.querySelector('input[type="email"]');
    if (input && !input.value.trim()) {
      input.focus();
      return;
    }
    const done = form.querySelector('[data-newsletter-done]');
    form.querySelectorAll('input, button').forEach((el) => (el.style.display = 'none'));
    if (done) done.removeAttribute('hidden');
  });
});
